import { getMDMentionValues } from '@sketchnote/minimal-editor';
import {
  AiContentState,
  useAiContent,
  useComments,
  useSlatePluginsStore,
} from '@sketchnote/slate-plugins';
import axios from 'axios';
import Config from 'config';
import * as React from 'react';
import { API, endpoints } from 'src/api';
import { useDocComments } from 'src/api/services/documents';
import { useLoaderStore } from 'src/components/loaders';
import { useAuth } from 'src/shared/hooks/use-auth';
import useSWR from 'swr';

const { useEffect } = React;

export const useSetStateBlockEditorValues = ({ docId }: { docId: string }) => {
  const { setIsLoading } = useLoaderStore();
  const { setMembers, setUploadImage, setCurrentUser, setOnMentionUser } = useSlatePluginsStore();
  const authData = useAuth((s) => s.userDetails);
  const { setOnFetchContent } = useAiContent();
  const { data: members } = useSWR('/user/members', API.get, { revalidateOnFocus: false });
  const { setCallbacks } = useComments();
  const { addCommentResponse, createCommentThread, deleteCommentThread, getCommentById } =
    useDocComments();

  useEffect(() => {
    setOnFetchContent(handleOnFetchAiContent);
    setUploadImage(handleUploadImage);
    setOnMentionUser(handleDocumentTagUser);
    setCallbacks?.({
      addCommentResponse: async (e) => {
        const mdUsers = getMDMentionValues(e.value);
        const res = await addCommentResponse({
          commentId: e.commentId,
          content: e.value,
          tags: mdUsers.values,
          text: mdUsers.md,
        });
        return res;
      },
      createCommentThread: (e) => {
        const mdUsers = getMDMentionValues(e.value);
        return createCommentThread({
          content: e.value,
          tags: mdUsers.values,
          text: mdUsers.md,
          creationId: docId,
        });
      },
      fetchComment: (e) => getCommentById({ id: e.id }),
      deleteCommentThread: (e) => deleteCommentThread({ id: e.id }),
    });
  }, [docId]);

  useEffect(() => {
    setMembers(members?.data?.data || []);
  }, [members]);

  useEffect(() => {
    setCurrentUser({
      avatar: authData?.avatarUrl || '',
      email: authData?.email || '',
      firstname: authData?.memberInformation.firstname || '',
      id: authData?.memberInformation?.memberId || '',
      lastname: authData?.memberInformation.lastname || '',
    });
  }, [authData]);

  const handleOnFetchAiContent: AiContentState['onFetchContent'] = async (query) => {
    const res = await API.get(endpoints.creationAi.url);
    if (res.data?.data?.length) {
      return { content: res.data.data, status: 'success' };
    }
    return { content: [], status: 'failed' };
  };

  const handleUploadImage = async (e) => {
    const data = new FormData();
    data.append('image', e);
    const response = await API.post('/creation/file', data, {
      headers: { ['Content-Type']: 'multipart/form-data' },
    });
    return response.data?.data?.message;
  };

  const handleDocumentTagUser = async ({ memberId }: { memberId: string }) => {
    await API.post('/creation/tag', { memberId: [memberId], creationId: docId });
  };

  async function handleDownloadPdf(url: string, title: string): Promise<void> {
    setIsLoading(true);
    const accessToken = useAuth.getState().accessToken;
    const lAPI = axios.create({ baseURL: Config.apiBase });

    lAPI.defaults.headers['Content-Type'] = 'application/pdf';
    lAPI.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
    lAPI.defaults.headers.Accept = 'application/pdf';
    lAPI.defaults.responseType = 'blob';
    const data = await lAPI.get(url);
    if (data.statusText === 'OK') {
      setIsLoading(false);
    }
    const element = document.createElement('a');
    const file = new Blob([data.data]);
    element.href = URL.createObjectURL(file);
    element.download = `${title}.pdf` ?? 'document';
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  }

  return {
    onDownloadPDF: handleDownloadPdf,
  };
};
